import React, { useState, useEffect } from 'react';
import { Bar, Line, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const AdminCampaignAnalyticsPage = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [summaryData, setSummaryData] = useState(null);
  const [performanceData, setPerformanceData] = useState(null);
  const [topCampaigns, setTopCampaigns] = useState([]);
  const [userStats, setUserStats] = useState(null);
  const [financialData, setFinancialData] = useState(null);

  useEffect(() => {
    fetchAllReports();
  }, [dateRange]);

  const fetchAllReports = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all reports in parallel
      const [summaryRes, performanceRes, topCampaignsRes, userStatsRes, financialRes] = await Promise.all([
        api.get('/api/campaign-reports/summary', {
          headers: { Authorization: `Bearer ${token}` }
        }),
        api.get('/api/campaign-reports/performance', {
          params: dateRange,
          headers: { Authorization: `Bearer ${token}` }
        }),
        api.get('/api/campaign-reports/top-campaigns', {
          headers: { Authorization: `Bearer ${token}` }
        }),
        api.get('/api/campaign-reports/user-statistics', {
          params: dateRange,
          headers: { Authorization: `Bearer ${token}` }
        }),
        api.get('/api/campaign-reports/financial-impact', {
          params: dateRange,
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (summaryRes.data.success) {
        setSummaryData(summaryRes.data.data);
      }

      if (performanceRes.data.success) {
        setPerformanceData(performanceRes.data.data);
      }

      if (topCampaignsRes.data.success) {
        setTopCampaigns(topCampaignsRes.data.data.campaigns);
      }

      if (userStatsRes.data.success) {
        setUserStats(userStatsRes.data.data);
      }

      if (financialRes.data.success) {
        setFinancialData(financialRes.data.data);
      }
    } catch (err) {
      console.error('Error fetching reports:', err);
      setError('Raporlar yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  // Chart data configurations
  const campaignTypePerformanceData = {
    labels: performanceData?.campaignTypeStats ? Object.keys(performanceData.campaignTypeStats) : [],
    datasets: [
      {
        label: 'Kullanım Sayısı',
        data: performanceData?.campaignTypeStats ? Object.values(performanceData.campaignTypeStats).map(stat => stat.count) : [],
        backgroundColor: 'rgba(75, 192, 192, 0.6)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 1
      }
    ]
  };

  const campaignTypeDistributionData = {
    labels: performanceData?.campaignTypeStats ? Object.keys(performanceData.campaignTypeStats) : [],
    datasets: [
      {
        data: performanceData?.campaignTypeStats ? Object.values(performanceData.campaignTypeStats).map(stat => stat.count) : [],
        backgroundColor: [
          '#FF6384',
          '#36A2EB',
          '#FFCE56',
          '#4BC0C0',
          '#9966FF',
          '#FF9F40'
        ],
        hoverBackgroundColor: [
          '#FF6384',
          '#36A2EB',
          '#FFCE56',
          '#4BC0C0',
          '#9966FF',
          '#FF9F40'
        ]
      }
    ]
  };

  const dailyUsageData = {
    labels: performanceData?.dailyUsage ? performanceData.dailyUsage.map(item => item.date) : [],
    datasets: [
      {
        label: 'Günlük Kullanım',
        data: performanceData?.dailyUsage ? performanceData.dailyUsage.map(item => item.count) : [],
        fill: true,
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        borderColor: 'rgba(75, 192, 192, 1)',
        tension: 0.4
      }
    ]
  };

  const topCampaignsData = {
    labels: topCampaigns.map(campaign => campaign.name || 'Kampanya'),
    datasets: [
      {
        label: 'Net İndirim (₺)',
        data: topCampaigns.map(campaign => campaign.netDiscount),
        backgroundColor: 'rgba(153, 102, 255, 0.6)',
        borderColor: 'rgba(153, 102, 255, 1)',
        borderWidth: 1
      }
    ]
  };

  const getCampaignTypeDisplayName = (type) => {
    const names = {
      cart: 'Sepet Kampanyası',
      category: 'Kategori İndirimi',
      flash_sale: 'Flash Sale',
      coupon: 'Kupon Kampanyası',
      package: 'Paket Kampanya',
      special_day: 'Özel Gün Kampanyası'
    };
    return names[type] || type;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Raporlar yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white p-8 rounded-xl shadow-lg max-w-md text-center">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">Bir hata oluştu</h3>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={fetchAllReports}
            className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors"
          >
            Tekrar Dene
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">📊 Kampanya Analiz Paneli</h1>
          <p className="text-gray-600">Kampanya performansı ve kullanıcı etkileşimleri hakkında detaylı analizler</p>
        </div>

        {/* Date Range Selector */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <h2 className="text-xl font-semibold text-gray-800">Tarih Aralığı</h2>
            <div className="flex flex-col sm:flex-row gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Başlangıç</label>
                <input
                  type="date"
                  value={dateRange.startDate}
                  onChange={(e) => setDateRange({...dateRange, startDate: e.target.value})}
                  className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bitiş</label>
                <input
                  type="date"
                  value={dateRange.endDate}
                  onChange={(e) => setDateRange({...dateRange, endDate: e.target.value})}
                  className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={fetchAllReports}
                  className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Raporları Güncelle
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        {summaryData && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Toplam İndirim</p>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(summaryData.performance.totalDiscountGiven)}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center">
                <div className="p-3 bg-green-100 rounded-lg">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Etkilenen Kullanıcı</p>
                  <p className="text-2xl font-bold text-gray-900">{summaryData.performance.uniqueUsers}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Aktif Kampanya</p>
                  <p className="text-2xl font-bold text-gray-900">{summaryData.performance.totalCampaigns}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center">
                <div className="p-3 bg-yellow-100 rounded-lg">
                  <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Ortalama ROI</p>
                  <p className="text-2xl font-bold text-gray-900">{summaryData.roi.averageROI.toFixed(2)}%</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Campaign Type Distribution */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Kampanya Türü Dağılımı</h3>
            <div className="h-80">
              <Pie data={campaignTypeDistributionData} options={{ responsive: true, maintainAspectRatio: false }} />
            </div>
          </div>

          {/* Daily Usage Trend */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Günlük Kullanım Trendi</h3>
            <div className="h-80">
              <Line data={dailyUsageData} options={{ responsive: true, maintainAspectRatio: false }} />
            </div>
          </div>
        </div>

        {/* Top Campaigns */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-800">En Başarılı Kampanyalar</h3>
            <button className="text-purple-600 hover:text-purple-800 font-medium">
              Tümünü Gör →
            </button>
          </div>
          <div className="h-96">
            <Bar data={topCampaignsData} options={{ responsive: true, maintainAspectRatio: false }} />
          </div>
        </div>

        {/* Detailed Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* User Statistics */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Kullanıcı İstatistikleri</h3>
            {userStats && (
              <div className="space-y-4">
                <div className="flex justify-between items-center pb-2 border-b border-gray-100">
                  <span className="text-gray-600">Toplam Kullanıcı</span>
                  <span className="font-semibold">{userStats.totalUsers}</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-gray-100">
                  <span className="text-gray-600">Ortalama Kampanya/Kullanıcı</span>
                  <span className="font-semibold">{userStats.statistics.averageCampaignsPerUser.toFixed(1)}</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-gray-100">
                  <span className="text-gray-600">Ortalama Tasarruf/Kullanıcı</span>
                  <span className="font-semibold">{formatCurrency(userStats.statistics.averageSavingsPerUser)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">En Aktif Kullanıcı</span>
                  <span className="font-semibold">
                    {userStats.statistics.mostActiveUserCampaigns > 0 ? 
                      `${userStats.statistics.mostActiveUserCampaigns} kampanya` : 
                      'Veri yok'}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Financial Impact */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Finansal Etki</h3>
            {financialData && (
              <div className="space-y-4">
                <div className="flex justify-between items-center pb-2 border-b border-gray-100">
                  <span className="text-gray-600">Toplam Verilen İndirim</span>
                  <span className="font-semibold text-red-600">{formatCurrency(financialData.totalDiscountGiven)}</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-gray-100">
                  <span className="text-gray-600">Toplam İade Edilen</span>
                  <span className="font-semibold text-green-600">{formatCurrency(financialData.totalRefunded)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 font-semibold">Net İndirim</span>
                  <span className="font-bold text-purple-600">{formatCurrency(financialData.netDiscount)}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCampaignAnalyticsPage;